import { Box, FormControlLabel, InputLabel, Typography } from '@mui/material';
import { DIFFERENT_COLOR, TYPE_ANALYTICS_MAP_VIEW } from 'common/defines/constants';
import PieChartCommon from 'components/Common/PieChartCommon';
import SkeletonCharts from 'components/Common/SkeletonCharts';
import { SwitchCustom } from 'components/Common/SwitchCustom';
import { useClientSettingsStyle } from 'components/Dashboard/ClientSettings/ClientSettingsStyle';
import { QUERY_KEY } from 'constants/constants';
import { get, isEmpty } from 'lodash';
import randomColor from 'randomcolor';
import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { getCustomFieldAnalytic, getSizeCustomFieldAnalytic } from 'services/analytics/apiAnalyticsConfig.services';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { changeSizeCustomFieldList, mapViewSelector, setDataCustomField } from 'store/slices/mapViewSlice';
import { rightBarSelector } from 'store/slices/rightBarSlice';
import CustomFieldCardLayer from '../../Layer/CustomField';
import { SwipeableViewsCustom } from '../../SwipeableViewsCustom';
import InfoTable from '../CropCover/InfoTable';

const CustomFieldAnalytics = () => {
  const dispatch = useAppDispatch();
  const [chartMode, setChartMode] = useState(false);
  const classes = useClientSettingsStyle();

  const { analyticId, sizeCustomFieldSelected, dataCustomField, sizeCustomFieldList } = useAppSelector(mapViewSelector);
  const { analyticReLabel } = useAppSelector(rightBarSelector);

  useQuery([QUERY_KEY.SIZE_CUSTOM_FIELD_ANALYTIC, analyticId], () => getSizeCustomFieldAnalytic(analyticId || ''), {
    enabled: !!analyticId && isEmpty(sizeCustomFieldList),
    onSuccess: (response) => {
      if (response.data) {
        dispatch(changeSizeCustomFieldList(response.data));
      }
    },
  });

  const { data: dataCropType } = useQuery(
    [QUERY_KEY.CUSTOM_FIELD_ANALYTIC, analyticId, sizeCustomFieldSelected],
    () => getCustomFieldAnalytic(analyticId || '', sizeCustomFieldSelected + ''),
    { enabled: !!analyticId && !!sizeCustomFieldSelected }
  );

  const dataPie = useMemo(() => {
    if (dataCustomField) {
      return dataCustomField.map((_item: any) => ({
        name: _item.type,
        value: _item.lduseArea,
        totalTree: _item.totalTree,
      }));
    }
    return [];
  }, [dataCustomField]);

  const getDataChart = useMemo(
    () =>
      dataCustomField?.map((_item: any) => ({
        name: _item.type,
        value: chartMode ? _item.totalTree : _item.lduseArea,
      })),
    [chartMode, dataCustomField]
  );

  const hasTreeCount = !!(dataCustomField?.length && dataCustomField.some((item: any) => item.totalTree > 0));

  const colorCustomField = useMemo(() => {
    if (dataCropType?.data) {
      let colors: string[] = [];
      dispatch(
        setDataCustomField(
          dataCropType.data.map((item: any, index: number) => {
            const colorRandom = randomColor({ luminosity: 'dark' });
            const _color = item.color || get(DIFFERENT_COLOR, `[${index}]`, colorRandom);
            colors.push(_color);
            return {
              ...item,
              visible: true,
              color: _color,
            };
          })
        )
      );
      return colors;
    }
    return [];
  }, [dataCropType, dispatch]);

  return (
    <SwipeableViewsCustom
      isDisableLabel
      analyticName={TYPE_ANALYTICS_MAP_VIEW.CUSTOM_FIELD}
      analyticTab={
        <>
          {dataCustomField?.length > 0 ? (
            <>
              <PieChartCommon
                title={analyticReLabel || 'Custom Field'}
                dataChart={getDataChart}
                colorCharts={colorCustomField}
              />
              {hasTreeCount && (
                <>
                  <InputLabel sx={{ marginLeft: '25px', fontSize: '14px' }}>Chart Mode:</InputLabel>
                  <FormControlLabel
                    sx={{ marginLeft: '30px' }}
                    control={
                      <SwitchCustom
                        checked={chartMode}
                        onClick={() => {
                          setChartMode(!chartMode);
                        }}
                      />
                    }
                    label={<Typography className={classes.textSetting}>{chartMode ? 'Tree Count' : 'Area'}</Typography>}
                  />
                </>
              )}
              <Box component={'div'} sx={{ marginTop: '30px' }}>
                <InfoTable dataPie={dataPie} hasTreeCount={hasTreeCount} />
              </Box>
            </>
          ) : (
            <>
              <SkeletonCharts count={1} />
            </>
          )}
        </>
      }
      layerTab={<CustomFieldCardLayer labelCard={analyticReLabel || TYPE_ANALYTICS_MAP_VIEW.CUSTOM_FIELD} />}
      isShowDownloadButton={dataCustomField?.length > 0}
    />
  );
};

export default CustomFieldAnalytics;
